import type { Theme } from "../types/theme.js";
import type { DocumentStyles } from "../types/template.js";

/** The built-in fallback theme. Any missing host theme value falls back to this. */
export const DEFAULT_THEME: Theme = {
  id: "default",
  name: "Default",
  typography: {
    headingFont: "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    bodyFont: "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    monoFont: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    baseFontSize: 11,
    headingScale: 1.25,
    headingWeight: 600,
    bodyWeight: 400,
    lineHeight: 1.5,
    headingLetterSpacing: -0.01
  },
  colors: {
    primary: "#1F3A5F",
    secondary: "#4A6FA5",
    accent: "#E0B04B",
    text: "#1A1D21",
    mutedText: "#6B7280",
    surface: "#FFFFFF",
    background: "#F5F6F8",
    border: "#E2E5EA",
    success: "#1E8E5A",
    warning: "#C77D0A",
    danger: "#C0392B",
    chart: ["#1F3A5F", "#4A6FA5", "#E0B04B", "#7FB3D5", "#A9CCE3", "#F4D03F", "#6C7A89", "#B0BEC5"]
  },
  radius: 6,
  table: {
    headerBackground: "#F3F4F6",
    headerColor: "#1A1D21",
    zebraBackground: "#FAFAFB",
    borderColor: "#E2E5EA"
  }
};

/** Document styles used by `createTemplate()`. Everything references theme tokens. */
export function defaultDocumentStyles(): DocumentStyles {
  return {
    text: {
      h1: { fontFamily: "$typography.headingFont", fontSize: 26, fontWeight: 600, lineHeight: 1.2, letterSpacing: -0.02, color: "$colors.primary", marginTop: 0, marginBottom: 8, keepWithNext: true },
      h2: { fontFamily: "$typography.headingFont", fontSize: 18, fontWeight: 600, lineHeight: 1.25, letterSpacing: -0.01, color: "$colors.primary", marginTop: 12, marginBottom: 6, keepWithNext: true },
      h3: { fontFamily: "$typography.headingFont", fontSize: 14, fontWeight: 600, lineHeight: 1.3, color: "$colors.text", marginTop: 8, marginBottom: 4, keepWithNext: true },
      h4: { fontFamily: "$typography.headingFont", fontSize: 12, fontWeight: 600, lineHeight: 1.35, color: "$colors.text", textTransform: "uppercase", letterSpacing: 0.04, marginTop: 8, marginBottom: 4, keepWithNext: true },
      body: { fontFamily: "$typography.bodyFont", fontSize: 11, fontWeight: 400, lineHeight: 1.5, color: "$colors.text", marginTop: 0, marginBottom: 0 },
      lead: { fontFamily: "$typography.bodyFont", fontSize: 13, fontWeight: 400, lineHeight: 1.55, color: "$colors.text", marginTop: 0, marginBottom: 0 },
      caption: { fontFamily: "$typography.bodyFont", fontSize: 9, fontWeight: 400, lineHeight: 1.4, color: "$colors.mutedText", marginTop: 0, marginBottom: 0 },
      small: { fontFamily: "$typography.bodyFont", fontSize: 8, fontWeight: 400, lineHeight: 1.4, color: "$colors.mutedText", marginTop: 0, marginBottom: 0 }
    },
    table: {
      fontSize: 10,
      headerBackground: "$table.headerBackground",
      headerColor: "$table.headerColor",
      headerFontWeight: 600,
      borderColor: "$table.borderColor",
      borderWidth: 1,
      borders: "horizontal",
      zebraBackground: "$table.zebraBackground",
      cellPaddingX: 8,
      cellPaddingY: 6
    },
    card: {
      background: "$colors.surface",
      borderColor: "$colors.border",
      borderWidth: 1,
      borderRadius: 6,
      padding: 16,
      shadow: false
    },
    link: { color: "$colors.secondary", underline: true },
    spacing: { blockGap: 8, sectionPadding: 0, columnGap: 16 }
  };
}
